import debounce from 'lodash/debounce';
import { GoogleMapsPartner } from '../components/GoogleMapsPartner';
import { Sidebar } from '../modules/Sidebar';

export class PartnerLocator {
    constructor(section = null) {
        if (!section) return;
        this.root = section;

        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onRootClick = this.onRootClick.bind(this);
        this.onPhraseFormInput = this.onPhraseFormInput.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onChannelsWheel = this.onChannelsWheel.bind(this);

        this.init();
    }

    init() {
        this.loader = this.root.querySelector('.loader');
        this.checkboxes =
            this.root.querySelectorAll('input[name="partner_channel"]') ?? [];
        this.jsOutput = this.root.querySelector('[js-content]');
        this.channelsTrack = this.root.querySelector(
            '[data-partner-channels-track]'
        );

        this.page = 1;
        this.phrase = '';
        this.activeType = Array.from(this.checkboxes).find(
            item => item.checked
        )?.value;
        this.events();
        this.fetchDebounced = debounce((...args) => this.fetch(...args), 300);

        this.mapInstance = null;
        this.mapWrapper = this.root.querySelector('[js-map-wrapper]');

        if (this.mapWrapper) {
            this.initMap(null);
        }
    }

    initMap(id = null) {
        if (this.mapInstance) {
            this.mapInstance.destroy();
        }

        this.mapWrapper = this.root.querySelector('[js-map-wrapper]');
        if (!this.mapWrapper) {
            this.mapInstance = null;
            return;
        }

        this.mapInstance = new GoogleMapsPartner(this.mapWrapper);
        this.mapInstance.onMarkerActivate = partnerId => {
            this.setActivePartnerCard(partnerId);
        };

        const clearActiveMarker = this.mapInstance.clearActiveMarker.bind(
            this.mapInstance
        );
        this.mapInstance.clearActiveMarker = () => {
            clearActiveMarker();
            this.setActivePartnerCard(null);
        };

        this.mapInstance.initMap(id);
    }

    isCompactLayout() {
        return Boolean(
            this.root?.closest?.('[data-partner-locator-sidebar]')
            || this.root?.matches?.('[data-partner-locator-sidebar]')
        );
    }

    setActivePartnerCard(partnerId) {
        this.root.querySelectorAll('[js-partner-card]').forEach(el => {
            el.classList.remove('-active');
            el.removeAttribute('data-active');
        });

        if (!partnerId) {
            return;
        }

        const card = this.root.querySelector(
            `[js-partner-card][data-id="${partnerId}"]`
        );
        if (card) {
            card.classList.add('-active');
            card.setAttribute('data-active', 'true');
        }
    }

    events() {
        this.checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', this.onCheckboxChange);
        });

        this.channelsTrack?.addEventListener('wheel', this.onChannelsWheel, {
            passive: false,
        });

        window.addEventListener('click', this.onRootClick);
        window.addEventListener('input', this.onPhraseFormInput);
        window.addEventListener('submit', this.onFormSubmit);
    }

    destroy() {
        this.checkboxes.forEach(checkbox => {
            checkbox.removeEventListener('change', this.onCheckboxChange);
        });

        this.channelsTrack?.removeEventListener('wheel', this.onChannelsWheel);

        window.removeEventListener('click', this.onRootClick);
        window.removeEventListener('input', this.onPhraseFormInput);
        window.removeEventListener('submit', this.onFormSubmit);

        if (this.mapInstance) {
            this.mapInstance.destroy();
            this.mapInstance = null;
        }
    }

    onChannelsWheel(e) {
        const track = this.channelsTrack;
        if (!track) return;

        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 1) return;

        const absX = Math.abs(e.deltaX);
        const absY = Math.abs(e.deltaY);
        const delta = absX > absY ? e.deltaX : e.deltaY;
        if (!delta) return;

        const prev = track.scrollLeft;
        const next = Math.min(maxScroll, Math.max(0, prev + delta));
        if (next === prev) return;

        e.preventDefault();
        track.scrollLeft = next;
    }

    onCheckboxChange(e) {
        this.activeType = e.target.value;
        this.phrase = '';
        this.page = 1;
        this.updateTypeInUrl(
            e.target.getAttribute('data-label') || e.target.value
        );
        this.fetch();
    }

    slugifyTypeLabel(label) {
        return String(label || '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    updateTypeInUrl(label) {
        const slug = this.slugifyTypeLabel(label);
        if (!slug) return;

        const url = new URL(window.location.href);
        url.searchParams.set('type', slug);
        window.history.replaceState({}, '', url.toString());
    }

    onRootClick(e) {
        const { target } = e;

        const card = target.closest('[js-partner-card]');
        if (card && this.root.contains(card)) {
            this.onPartnerCardClick(card);
            return;
        }

        const button = target.closest('.js-partner-pagination-button');
        if (button && this.root.contains(button)) {
            this.onPaginationButtonClick(button);
        }
    }

    onPartnerCardClick(card) {
        const cardId = card.dataset.id;
        if (!cardId) return;

        this.setActivePartnerCard(cardId);
        this.scrollToMap();

        if (this.mapInstance?.markers?.length) {
            this.mapInstance.goToMarker(cardId);
        }
    }

    getNavbarOffset() {
        const sources = [document.body, document.documentElement];

        for (const el of sources) {
            if (!el) continue;

            const raw = getComputedStyle(el)
                .getPropertyValue('--navbar-height')
                .trim();
            const parsed = Number.parseFloat(raw);

            if (Number.isFinite(parsed) && parsed > 0) {
                return parsed;
            }
        }

        return 80;
    }

    scrollToMap() {
        const mapTarget =
            this.root.querySelector('[js-map-shell]') ||
            this.root.querySelector('[js-map-wrapper]');

        if (!mapTarget) {
            return;
        }

        const scrollParent = mapTarget.closest(
            '.partner-locator-sidebar__body, .minicart-sidebar__body'
        );
        // Scroll further up by navbar height so the map sits below the menu.
        const offset = this.getNavbarOffset();

        if (scrollParent) {
            const parentRect = scrollParent.getBoundingClientRect();
            const targetRect = mapTarget.getBoundingClientRect();
            const top =
                scrollParent.scrollTop +
                (targetRect.top - parentRect.top) -
                offset;

            scrollParent.scrollTo({
                top: Math.max(0, top),
                behavior: 'smooth',
            });
            return;
        }

        const top =
            window.scrollY +
            mapTarget.getBoundingClientRect().top -
            offset;

        window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth',
        });
    }

    getInitialPartnerCardId() {
        const activeCard = this.root.querySelector('[js-partner-card].-active');
        return activeCard?.dataset.id ?? null;
    }

    onPhraseFormInput(e) {
        const { target } = e;
        const form = target.closest('[js-phrase-form]');
        if (!form || !this.root.contains(form)) return;

        this.phrase = target.value;
        this.page = 1;

        this.fetchDebounced(
            true,
            this.root.querySelector('[js-partners-list-section]'),
            true,
            true
        );
    }

    onFormSubmit(e) {
        const { target } = e;
        const form = target.closest('[js-phrase-form]');
        if (!form || !this.root.contains(form)) return;

        e.preventDefault();
    }

    async onPaginationButtonClick(button) {
        const paginationElement = button.closest('[js-pagination]');
        const buttonWrapper = button.closest('[js-pagination-button-wrapper]');
        if (!paginationElement || !buttonWrapper) return;

        this.page = this.page + 1;
        await this.fetch(false, this.root.querySelector('[js-partners-list]'));

        const current = paginationElement.querySelector('[js-current]');
        const progressBar =
            paginationElement.querySelector('[js-progress-bar]');
        const totalElement = parseInt(
            paginationElement.getAttribute('data-total')
        );
        const elements = this.root.querySelectorAll('[js-partner-card]');

        current.textContent = elements.length;
        progressBar.style.width = `${(elements.length / totalElement) * 100}%`;

        paginationElement.setAttribute('data-page', this.page);
        if (this.page == paginationElement.getAttribute('data-max-pages')) {
            buttonWrapper.classList.add('hidden');
        } else {
            buttonWrapper.classList.remove('hidden');
        }
    }

    async fetch(
        replaceContent = true,
        htmlOutput = this.jsOutput,
        isFocus = false,
        withoutMapRefresh = false
    ) {
        try {
            this.loader?.classList.add('active');
            const formData = new FormData();
            formData.append('action', 'fetch_partner_locator_content');
            formData.append('phrase', this.phrase);
            formData.append('type', this.activeType);
            formData.append('page', this.page);
            if (withoutMapRefresh) {
                formData.append('onlyRightColumn', true);
            }
            if (this.isCompactLayout()) {
                formData.append('compact', '1');
            }
            const req = await fetch(ajax.url, {
                method: 'POST',
                body: formData,
            });
            const res = await req.json();

            if (!withoutMapRefresh && this.page == 1 && this.mapInstance) {
                this.mapInstance.destroy();
                this.mapInstance = null;
            }

            if (replaceContent) {
                htmlOutput.innerHTML = res.html;
            } else {
                htmlOutput.innerHTML += res.html;
            }

            this.mapWrapper = this.root.querySelector('[js-map-wrapper]');

            if (this.mapWrapper && this.page == 1) {
                const activeId = this.getInitialPartnerCardId();
                if (withoutMapRefresh) {
                    if (this.mapInstance && Array.isArray(res.points)) {
                        await this.mapInstance.setPoints(res.points, {
                            activeId,
                            fit: true,
                        });
                    } else if (activeId && this.mapInstance) {
                        this.mapInstance.goToMarker(activeId);
                    } else if (this.mapInstance) {
                        this.mapInstance.clearActiveMarker();
                    }
                } else {
                    this.initMap(activeId);
                }
            }

            if (isFocus) {
                const input = this.root.querySelector('[name="phrase"]');
                if (input) {
                    input.focus();
                    input.setSelectionRange(
                        input.value.length,
                        input.value.length
                    );
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.loader?.classList.remove('active');
        }
    }
}

class PartnerLocatorSidebar {
    constructor() {
        if (typeof ajax === 'undefined') {
            return;
        }

        this.sidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });
        this.contentHost = document.createElement('div');
        this.contentHost.className = 'h-full w-full max-w-[720px]';
        this.sidebar.content.appendChild(this.contentHost);
        this.isLoading = false;
        this.locator = null;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('keydown', this.onKeyDown, true);
    }

    async onDocumentClick(event) {
        const trigger = event.target.closest(
            '[data-open-partner-locator-sidebar]'
        );

        if (!trigger) {
            return;
        }

        event.preventDefault();
        await this.openSidebar();
    }

    onKeyDown(event) {
        if (event.key !== 'Escape' || !this.sidebar?.isOpen) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        // First Esc closes the map popup; second closes the sidebar.
        if (this.locator?.mapInstance?.activeMarker) {
            this.locator.mapInstance.clearActiveMarker();
            return;
        }

        this.destroyLocator();
        this.sidebar.close();
    }

    async openSidebar() {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.destroyLocator();
        this.contentHost.innerHTML =
            '<div class="flex h-full w-full max-w-[720px] items-center justify-center bg-background-primary"><p class="text-regular text-text-secondary">...</p></div>';

        try {
            const html = await this.fetchSidebarHtml();

            if (!html) {
                return;
            }

            this.contentHost.innerHTML = html;

            const root =
                this.contentHost.querySelector('.partner-locator') ||
                this.contentHost.querySelector('[data-partner-locator-sidebar]');

            if (root) {
                this.locator = new PartnerLocator(root);
            }

            this.sidebar.open();
        } finally {
            this.isLoading = false;
        }
    }

    destroyLocator() {
        if (this.locator) {
            this.locator.destroy();
            this.locator = null;
        }
    }

    async fetchSidebarHtml() {
        const formData = new FormData();
        formData.append('action', 'mda_partner_locator_sidebar');
        formData.append('nonce', ajax.nonce);

        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.html !== 'string') {
            return null;
        }

        return result.data.html;
    }
}

const sections = document.querySelectorAll('section.partner-locator');
sections.forEach(section => {
    new PartnerLocator(section);
});

new PartnerLocatorSidebar();
